import apiClient from '../lib/api/client';

export interface BookMateriality {
  id: number;
  name: string;
}

export interface CategoryRef {
  id: number;
  code: string;
}

export interface BookListItem {
  id: number;
  title: string;
  isbn: string | null;
  sankhya_code: string | number | null;
  primary_categories: CategoryRef[];
  secondary_categories: CategoryRef[];
  materiality: BookMateriality | null;
  editorial_complete?: boolean;
  status?: 'active' | 'inactive';
  eligible_for_simulation: 'apto' | 'inapto' | boolean;
}

export interface BooksPagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

export interface BooksListResponse {
  message: string;
  data: BookListItem[];
  pagination: BooksPagination;
}

export interface BooksListParams {
  page?: number;
  per_page?: number;
  search?: string;
  title?: string;
  category_ids?: number[];
  primary_category_ids?: number[];
  secondary_category_ids?: number[];
  materiality_id?: number;
  materiality_ids?: number[];
  csat_min?: number | null;
  csat_max?: number;
  cost?: number;
  cost_min?: number;
  cost_max?: number;
  contract_type?: 'determinate' | 'indeterminate';
  contract_expiration?: string;
  expiration_from?: string;
  expiration_to?: string;
  status?: 'active' | 'inactive';
  contract_status?: 'active' | 'expired';
  apto_simulacao?: boolean;
  eligibility?: 'apto' | 'inapto';
  pending_contract?: boolean;
  pending_cost?: boolean;
  pending_editorial?: boolean;
  editorial_complete?: boolean;
  simulation_eligible?: boolean;
}

function buildBooksQueryString(params: BooksListParams): string {
  const searchParams = new URLSearchParams();

  if (params.page != null) searchParams.set('page', String(params.page));
  if (params.per_page != null) searchParams.set('per_page', String(params.per_page));
  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.title?.trim() && !params.search?.trim()) searchParams.set('title', params.title.trim());
  if (params.materiality_id != null) searchParams.set('materiality_id', String(params.materiality_id));
  if (params.csat_min === null) {
    searchParams.set('csat_min', 'null');
  } else if (params.csat_min != null) {
    searchParams.set('csat_min', String(params.csat_min));
  }
  if (params.csat_max != null) searchParams.set('csat_max', String(params.csat_max));
  if (params.cost != null) searchParams.set('cost', String(params.cost));
  if (params.cost_min != null) searchParams.set('cost_min', String(params.cost_min));
  if (params.cost_max != null) searchParams.set('cost_max', String(params.cost_max));
  if (params.contract_type) searchParams.set('contract_type', params.contract_type);
  if (params.contract_expiration) searchParams.set('contract_expiration', params.contract_expiration);
  if (params.expiration_from) searchParams.set('expiration_from', params.expiration_from);
  if (params.expiration_to) searchParams.set('expiration_to', params.expiration_to);
  if (params.status) searchParams.set('status', params.status);
  if (params.contract_status) searchParams.set('contract_status', params.contract_status);
  if (params.apto_simulacao === true) searchParams.set('apto_simulacao', '1');
  if (params.eligibility) searchParams.set('eligibility', params.eligibility);
  if (params.editorial_complete != null) {
    searchParams.set('editorial_complete', String(params.editorial_complete));
  }
  if (params.simulation_eligible != null) {
    searchParams.set('simulation_eligible', String(params.simulation_eligible));
  }
  if (params.pending_contract === true) searchParams.set('pending_contract', '1');
  if (params.pending_cost === true) searchParams.set('pending_cost', '1');
  if (params.pending_editorial === true) searchParams.set('pending_editorial', '1');

  if (params.category_ids?.length) {
    params.category_ids.forEach((id) => searchParams.append('category_ids[]', String(id)));
  }
  if (params.primary_category_ids?.length) {
    params.primary_category_ids.forEach((id) =>
      searchParams.append('primary_category_ids[]', String(id))
    );
  }
  if (params.secondary_category_ids?.length) {
    params.secondary_category_ids.forEach((id) =>
      searchParams.append('secondary_category_ids[]', String(id))
    );
  }
  if (params.materiality_ids?.length) {
    params.materiality_ids.forEach((id) => searchParams.append('materiality_ids[]', String(id)));
  }

  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
}

const defaultPagination: BooksPagination = {
  current_page: 1,
  per_page: 15,
  total: 0,
  last_page: 1,
  from: 0,
  to: 0,
};

function normalizeListResponse(raw: unknown): BooksListResponse {
  if (!raw || typeof raw !== 'object') {
    return { message: '', data: [], pagination: defaultPagination };
  }
  const r = raw as Record<string, unknown>;
  // Resposta direta: { message, data: [], pagination }
  let list: unknown = r.data ?? r.items ?? r.results ?? r.books;
  let pagination = r.pagination ?? r.meta;
  let message = typeof r.message === 'string' ? r.message : '';
  // Envelope extra (ex.: Laravel): { data: { message, data: [], pagination } }
  if (list && typeof list === 'object' && !Array.isArray(list)) {
    const inner = list as Record<string, unknown>;
    const innerList = inner.data ?? inner.items ?? inner.results ?? inner.books;
    if (innerList !== undefined) {
      list = innerList;
      if (pagination == null) pagination = inner.pagination ?? inner.meta;
      if (!message && typeof inner.message === 'string') message = inner.message;
    }
  }
  const data = Array.isArray(list) ? (list as BookListItem[]) : [];
  const pag =
    pagination && typeof pagination === 'object' && pagination !== null
      ? (pagination as BooksPagination)
      : defaultPagination;
  return { message, data, pagination: pag };
}

export interface CreateBookBody {
  title: string;
  cost: number;
  materiality_id: number;
  status: 'active' | 'inactive';
  contract_type: 'determinate' | 'indeterminate';
  contract_expiration?: string | null;
}

export interface UpdateBookBody {
  title?: string;
  cost?: number;
  materiality_id?: number;
  status?: 'active' | 'inactive';
  contract_type?: 'determinate' | 'indeterminate';
  contract_expiration?: string | null;
}

export interface BookDetail {
  id: number;
  title: string;
  cost: number | null;
  materiality: BookMateriality | null;
  status: 'active' | 'inactive';
  contract_type: 'determinate' | 'indeterminate' | null;
  contract_expiration: string | null;
  eligible_for_simulation: 'apto' | 'inapto' | boolean;
  isbn?: string | null;
  sankhya_code?: string | number | null;
  primary_categories?: CategoryRef[];
  secondary_categories?: CategoryRef[];
}

export interface BookDetailResponse {
  message: string;
  data: BookDetail;
}

export interface ToggleStatusResponse {
  message: string;
  data?: Record<string, unknown>;
}

export interface UpdateSimulationEligibilityBody {
  simulation_eligible: boolean;
}

export interface BookVersionItem {
  id?: number;
  book_id?: number;
  erp_code: string | number;
  isbn: string;
}

export interface BookVersionResponse {
  message?: string;
  data?: BookVersionItem[] | BookVersionItem;
}

export interface CreateBookVersionBody {
  book_id: number;
  erp_code: string | number;
  isbn: string;
}

export interface UpdateBookVersionBody {
  erp_code: string | number;
  isbn: string;
}

export interface BookCategoryItem {
  /** ID da relação livro–categoria (para PUT /book-categories/{id}) */
  id?: number;
  category_id: number;
  csat?: number | null;
  is_new?: boolean | null;
  is_main?: boolean | null;
}

export interface UpdateBookCategoryBody {
  csat?: number | null;
  is_new?: boolean;
  is_main?: boolean;
}

export interface CreateBookCategoriesBody {
  book_id: number;
  categories: BookCategoryItem[];
}

export interface BookCategoriesResponse {
  message?: string;
  data?: BookCategoryItem[] | { categories?: BookCategoryItem[] };
}

export const booksService = {
  async getList(params: BooksListParams = {}): Promise<BooksListResponse> {
    const query = buildBooksQueryString(params);
    const response = await apiClient.get<unknown>(`/books${query}`);
    return normalizeListResponse(response);
  },

  async getById(id: number): Promise<BookDetailResponse> {
    return apiClient.get<BookDetailResponse>(`/books/${id}`);
  },

  async create(body: CreateBookBody): Promise<BookDetailResponse> {
    return apiClient.post<BookDetailResponse>('/books', body);
  },

  async update(id: number, body: UpdateBookBody): Promise<BookDetailResponse> {
    return apiClient.put<BookDetailResponse>(`/books/${id}`, body);
  },

  async getBookVersions(bookId: number): Promise<BookVersionResponse> {
    return apiClient.get<BookVersionResponse>(`/book-versions/${bookId}`);
  },

  async createBookVersion(body: CreateBookVersionBody): Promise<BookVersionResponse> {
    return apiClient.post<BookVersionResponse>('/book-versions', body);
  },

  async updateBookVersion(
    bookVersionId: number,
    body: UpdateBookVersionBody
  ): Promise<BookVersionResponse> {
    return apiClient.put<BookVersionResponse>(`/book-versions/${bookVersionId}`, body);
  },

  async getBookCategories(bookId: number): Promise<BookCategoriesResponse> {
    return apiClient.get<BookCategoriesResponse>(`/book-categories/${bookId}`);
  },

  async createBookCategories(
    body: CreateBookCategoriesBody
  ): Promise<BookCategoriesResponse> {
    return apiClient.post<BookCategoriesResponse>('/book-categories', body);
  },

  async updateBookCategory(
    bookCategoryId: number,
    body: UpdateBookCategoryBody
  ): Promise<BookCategoriesResponse> {
    return apiClient.put<BookCategoriesResponse>(
      `/book-categories/${bookCategoryId}`,
      body
    );
  },

  async deleteBookCategory(bookCategoryId: number): Promise<void> {
    await apiClient.delete(`/book-categories/${bookCategoryId}`);
  },

  async toggleStatus(id: number): Promise<ToggleStatusResponse> {
    return apiClient.put<ToggleStatusResponse>(`/books/${id}/status`);
  },

  async updateSimulationEligibility(
    id: number,
    body: UpdateSimulationEligibilityBody
  ): Promise<ToggleStatusResponse> {
    return apiClient.patch<ToggleStatusResponse>(
      `/books/${id}/simulation-eligible`,
      body
    );
  },
};

export default booksService;
